import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OfficeTaskDetail } from "@/components/office/OfficeTaskDetail";
import { TaskChip } from "./MyWork";

const BUCKETS = [
  { key: "today", label: "Today", cls: "text-signal-green" },
  { key: "1_2_days", label: "1–2 Days", cls: "text-foreground" },
  { key: "3_7_days", label: "3–7 Days", cls: "text-foreground" },
  { key: "8_15_days", label: "8–15 Days", cls: "text-signal-amber" },
  { key: "15_30_days", label: "15–30 Days", cls: "text-signal-amber" },
  { key: "over_30_days", label: "30+ Days", cls: "text-signal-red" },
];

export default function OfficeAgeing() {
  const [buckets, setBuckets] = useState(null);
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ department: "", assignee: "", priority: "", status: "" });
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const [a, m, u] = await Promise.all([
      api.get("/office/reports/ageing", { params }),
      api.get("/office/meta"),
      api.get("/office/directory").catch(() => ({ data: [] })),
    ]);
    setBuckets(a.data);
    setMeta(m.data);
    setUsers(u.data);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-5" data-testid="ageing-page">
      <SectionTitle>Task Ageing</SectionTitle>
      <div className="flex flex-wrap gap-3">
        <Select value={filters.department || "all"} onValueChange={(v) => setF("department", v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="ageing-dept"><SelectValue placeholder="All departments" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All departments</SelectItem>
            {(meta?.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.assignee || "all"} onValueChange={(v) => setF("assignee", v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="ageing-employee"><SelectValue placeholder="All employees" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All employees</SelectItem>
            {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.priority || "all"} onValueChange={(v) => setF("priority", v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="ageing-priority"><SelectValue placeholder="All priorities" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All priorities</SelectItem>
            {(meta?.priorities || []).map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setF("status", v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="ageing-status"><SelectValue placeholder="Open statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Open statuses</SelectItem>
            {["not_started", "in_progress", "blocked", "on_hold"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!buckets ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="space-y-5">
          {BUCKETS.map(({ key, label, cls }) => (
            <div key={key} data-testid={`ageing-${key}`}>
              <h3 className={`text-base font-semibold uppercase tracking-tight mb-2 ${cls}`}>
                {label} <span className="metric text-sm">({buckets[key].length})</span>
              </h3>
              {buckets[key].length === 0 ? <p className="text-xs text-muted-foreground">None</p> : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                  {buckets[key].map((t) => <TaskChip key={t.task_id} t={t} onClick={() => setDetailId(t.task_id)} />)}
                </div>)}
            </div>
          ))}
        </div>)}
      <OfficeTaskDetail taskId={detailId} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
}
